import React, { createContext, useState, useEffect } from 'react';
import { buildApiUrl } from '../config/api';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);

    useEffect(() => {
        const checkAuth = async () => {
            const token = localStorage.getItem('token');

            if (!token) {
                setAuthLoading(false);
                return;
            }

            try {
                const response = await fetch(buildApiUrl('/api/users/check'), {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const data = await response.json();
                if (data.isAuthenticated) {
                    setIsAuthenticated(true);
                    setUser(data.user);
                } else {
                    setIsAuthenticated(false);
                    setUser(null);
                }
            } catch (err) {
                console.error(err);
                setIsAuthenticated(false);
                setUser(null);
            } finally {
                setAuthLoading(false);
            }
        };
        checkAuth();
    }, []);

    const register = async (username, password) => {
        try {
            const response = await fetch(buildApiUrl('/api/users/register'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password })
            });
            const data = await response.json();
    
            if (data.user) {
                // Automatically sign in the user after successful registration
                const signInResponse = await signIn(username, password);
                if (signInResponse.token) {
                    setIsAuthenticated(true);
                    setUser({ username, id: data.user.id });
                    localStorage.setItem('token', signInResponse.token);
                    return { success: true }; // Return success if the sign-in process worked
                }
            }
            return { error: data.error || data.message || 'Registration failed' };
        } catch (err) {
            console.error(err);
            return { error: 'An error occurred during registration' };
        }
    };
    

    const signIn = async (username, password) => {
        try {
            const response = await fetch(buildApiUrl('/api/users/login'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password })
            });
            const data = await response.json();
            if (data.token) {
                setIsAuthenticated(true);
                setUser({ username, id: data.userId, role: data.role });
                localStorage.setItem('token', data.token);
            }
            return data;
        } catch (err) {
            console.error(err);
            return { error: err.message };
        }
    };

    const registerFromOrder = async ({ username, password, orderId, customerAccessToken }) => {
        try {
            const response = await fetch(buildApiUrl('/api/users/register-from-order'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password, orderId, customerAccessToken }),
            });
            const data = await response.json();
            if (!response.ok || !data.token) return { error: data.error || 'Account creation failed' };
            setIsAuthenticated(true);
            setUser({ username, id: data.userId, role: data.role });
            localStorage.setItem('token', data.token);
            return { success: true };
        } catch (err) {
            return { error: 'An error occurred while creating your account' };
        }
    };

    const signOut = async () => {
        try {
            await fetch(buildApiUrl('/api/users/logout'), { method: 'POST' });
            setIsAuthenticated(false);
            setUser(null);
            localStorage.removeItem('token');
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <AuthContext.Provider value={{ isAuthenticated, user, authLoading, register, registerFromOrder, signIn, signOut }}>
            {children}
        </AuthContext.Provider>
    );
};
